/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Circle, Fill, Icon, RegularShape, Stroke, Style } from 'ol/style';

import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, mergeMapTo, take } from 'rxjs/operators';
import { GeometryCategory, PointStyle, PolygonStyle, StyleCollection, StyleConfiguration } from '../../../types';
import { RestService } from '../../shared/services/rest.service';
import { Actions, Select, Store } from '@ngxs/store';
import { SetGroupVisibility, SetLayerVisibility } from '../state/map-config.actions';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { MapConfigState } from '../state/map-config.state';
import { LoggerService } from '../../shared/services/logger.service';
import { MsGroupNode, MsLayerNode } from '../state/map-config.model';
import { RedlineState } from '../../../components/redline/state/redline.state';
import { RedlineLayerConfiguration } from '../../../components/redline/state/redline.model';
import { Extent } from 'ol/extent';
import { Coordinate } from 'ol/coordinate';
import { MatIconRegistry } from '@angular/material/icon';
import { ShowError } from '../../../core/state/notification/notification.actions';

@Injectable()
export class MapConfigService {
    private _styles = new Map<string, Map<GeometryCategory, Style[]>>();

    @Select(MapConfigState.root) private root$: Observable<MsGroupNode>;
    @Select(RedlineState.viewable) private redlineLayers$: Observable<RedlineLayerConfiguration[]>;

    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private actions$: Actions,
        private store: Store,
        private iconRegistry: MatIconRegistry
    ) {}

    public getAllLayers$(): Observable<MsLayerNode[]> {
        return this.root$.pipe(
            map((root) =>
                MapConfigService.traverseLayers(
                    [root],
                    null,
                    (layerName, layer) => layer,
                    () => true
                )
            )
        );
    }

    public getBaseLayer() {
        return this.store.selectSnapshot(AppConfigState.baseLayer);
    }

    /**
     *
     * @param scale
     * @returns {*}
     */
    public getVisibleLayerList(scale): MsLayerNode[] {
        const root = this.store.selectSnapshot(MapConfigState.root);
        return MapConfigService.traverseLayers(
            [root],
            null,
            (layerName, layer) => {
                for (const layerClass of layer.classes) {
                    if (scale >= layerClass.minScale && scale < layerClass.maxScale) {
                        return layer.status ? layer : false;
                    }
                }
                return false;
            },
            (groupName, group) => group.status !== false
        );
    }

    public getSelectableLayers(scale): MsLayerNode[] {
        const root = this.store.selectSnapshot(MapConfigState.root);
        return MapConfigService.traverseLayers(
            [root],
            null,
            (layerName: string, layer: MsLayerNode) => {
                for (const layerClass of layer.classes) {
                    if (scale >= layerClass.minScale && scale < layerClass.maxScale) {
                        return layer.status && layer.selectable ? layer : false;
                    }
                }
                return false;
            },
            (groupName: string, group: MsGroupNode) => group.status
        );
    }

    public getAllSelectableLayerNames$(): Observable<string[]> {
        const layerNames$ = combineLatest([this.getSelectableMsLayers$(), this.redlineLayers$]).pipe(
            map(([msLayers, redlineLayers]: [MsLayerNode[], RedlineLayerConfiguration[]]) =>
                [].concat(
                    msLayers.map((l) => l.name),
                    redlineLayers.map((l) => l.name)
                )
            )
        );

        return this.store.select(RedlineState.loaded).pipe(
            filter((b) => b),
            mergeMapTo(layerNames$),
            take(1)
        );
    }

    public getSelectableLayers$(scale): Observable<MsLayerNode[]> {
        return this.root$.pipe(
            map((root) =>
                MapConfigService.traverseLayers(
                    [root],
                    null,
                    (layerName: string, layer: MsLayerNode) => {
                        for (const layerClass of layer.classes) {
                            if (scale >= layerClass.minScale && scale < layerClass.maxScale) {
                                return layer.status && layer.selectable ? layer : false;
                            }
                        }
                        return false;
                    },
                    (groupName: string, group: MsGroupNode) => group.status !== false
                )
            )
        );
    }

    public getLayer(name: string): MsLayerNode {
        const root = this.store.selectSnapshot(MapConfigState.root);
        const layerArray = MapConfigService.traverseLayers(
            [root],
            name,
            (_: string, layer: MsLayerNode) => layer,
            () => true
        );
        if (layerArray.length === 0) {
            this.store.dispatch(new ShowError(`Layer [${name}] was not found.`));
        }
        if (layerArray.length === 1) {
            return layerArray[0];
        }
    }

    public getLayer$(name): Observable<MsLayerNode> {
        return this.root$.pipe(
            map((root) => {
                const layerArray = MapConfigService.traverseLayers(
                    [root],
                    name,
                    (layerName, layer) => layer,
                    () => true
                );
                if (layerArray.length === 1) {
                    return layerArray[0];
                }
            })
        );
    }

    public showLayer(name, force = false) {
        this.store.dispatch(new SetLayerVisibility(name, true, force));
    }

    public hideLayer(name) {
        this.store.dispatch(new SetLayerVisibility(name, false));
    }

    public showGroup(name) {
        this.store.dispatch(new SetGroupVisibility(name, true));
    }

    public hideGroup(name) {
        this.store.dispatch(new SetGroupVisibility(name, false));
    }

    public expandGroup(name) {
        const root = this.store.selectSnapshot(MapConfigState.root);
        MapConfigService.traverseGroups(root, name, (groupName, group) => {
            group.expand = true;
            return group;
        });
    }

    public collapseGroup(name) {
        const root = this.store.selectSnapshot(MapConfigState.root);
        MapConfigService.traverseGroups(root, name, (groupName, group) => {
            group.expand = false;
            return group;
        });
    }

    public expandLayer(name) {
        const root = this.store.selectSnapshot(MapConfigState.root);
        MapConfigService.traverseLayers(
            [root],
            name,
            (layerName, layer) => {
                layer.expand = true;
            },
            () => true
        );
    }

    public collapseLayer(name) {
        const root = this.store.selectSnapshot(MapConfigState.root);
        MapConfigService.traverseLayers(
            [root],
            name,
            (layerName, layer) => {
                layer.expand = false;
            },
            () => true
        );
    }

    public getResFromScaleDenom(scaleDenomination, unit, dpi) {
        const dotsPerUnit = dpi * this.getInchPerUnit(unit);
        return scaleDenomination / dotsPerUnit;
    }

    public getScaleDenominationForResolution(resolution, unit, dpi = 96) {
        const dotsPerUnit = dpi * this.getInchPerUnit(unit);
        return resolution * dotsPerUnit;
    }

    public getInchPerUnit(unit) {
        const units = {
            ft: 12.0,
            in: 1.0,
            m: 39.370078740157,
            mi: 63360,
            nm: 72913.385826772,
            'us-ft': 12.000024,
        };
        return units[unit];
    }

    public getLayerStyles(layerName): Map<GeometryCategory, Style[]> {
        return this._styles.get(layerName);
    }

    public getAllStyles(): StyleCollection {
        return this.store.selectSnapshot(MapConfigState.styles);
    }

    public getStyles(name: string, type: GeometryCategory, config: StyleConfiguration[] = null): Style[] {
        const allStyles = this.getAllStyles();
        let styleTypes = this._styles.get(name);
        let style;
        if (!styleTypes) {
            styleTypes = new Map<GeometryCategory, Style[]>();
        } else {
            style = styleTypes.get(type);
        }
        if (!style) {
            const styleConfigs: StyleConfiguration[] = config || (allStyles[name] ? allStyles[name][type] : null);
            if (styleConfigs) {
                style = this.createStyle(styleConfigs);
                styleTypes.set(type, style);
                this._styles.set(name, styleTypes);
            } else {
                style = this.createDefaultStyle(type);
            }
        }
        return style;
    }

    public createStyle(styleConfigs: StyleConfiguration[]): Style[] {
        const olStyles: Style[] = [];
        styleConfigs.forEach((styleConfig, i) => {
            let olStyle;
            switch (styleConfig.type) {
                case 'line':
                    olStyle = this.createLineStyle(styleConfig);
                    break;
                case 'polygon':
                    olStyle = this.createPolygonStyle(styleConfig);
                    break;
                case 'point-circle':
                case 'point-polygon':
                case 'point-star':
                case 'point-icon':
                    olStyle = this.createPointImageStyle(styleConfig);
            }
            olStyle.setZIndex(i + 1);
            olStyles.push(olStyle);
        });
        return olStyles;
    }

    private createDefaultStyle(type: GeometryCategory): Style[] {
        let olStyle = null;

        const allStyles = this.getAllStyles();
        if (allStyles['default'] && allStyles['default'][type]) {
            return this.createStyle(allStyles['default'][type]);
        }

        switch (type) {
            case GeometryCategory.Polygon:
                olStyle = new Style({
                    fill: new Fill({
                        color: [255, 200, 0, 0.9],
                    }),
                    stroke: new Stroke({
                        color: [205, 200, 0, 0.9],
                        width: 2,
                    }),
                });
                break;
            case GeometryCategory.Line:
                olStyle = new Style({
                    stroke: new Stroke({
                        color: [0, 0, 0, 0.9],
                        lineDash: [2, 3],
                        width: 2,
                    }),
                });
                break;
            case GeometryCategory.Point:
                olStyle = new Style({
                    image: new Circle({
                        fill: new Fill({
                            color: [255, 200, 0, 0.3],
                        }),
                        radius: 3,
                        stroke: new Stroke({
                            color: [255, 200, 0, 0.9],
                            width: 1,
                        }),
                    }),
                });
        }
        return [olStyle];
    }

    private createLineStyle(styleConfig): Style {
        return new Style({
            ...(styleConfig.stroke && { stroke: new Stroke(styleConfig.stroke) }),
        });
    }

    private createPolygonStyle(styleConfig: PolygonStyle) {
        return new Style({
            ...(styleConfig.fill && { fill: new Fill(styleConfig.fill) }),
            ...(styleConfig.stroke && {
                stroke: new Stroke(styleConfig.stroke),
            }),
        });
    }

    private createPointImageStyle(styleConfig: PointStyle) {
        let imageStyle;
        switch (styleConfig.type) {
            case 'point-circle':
                imageStyle = new Circle({
                    radius: styleConfig.radius,
                    ...(styleConfig.fill && { fill: new Fill(styleConfig.fill) }),
                    ...(styleConfig.stroke && {
                        stroke: new Stroke(styleConfig.stroke),
                    }),
                });
                break;
            case 'point-polygon':
                imageStyle = new RegularShape({
                    points: styleConfig.sides,
                    radius: styleConfig.radius,
                    ...(styleConfig.angle && { angle: styleConfig.angle }),
                    ...(styleConfig.fill && {
                        fill: new Fill(styleConfig.fill),
                    }),
                    ...(styleConfig.stroke && { stroke: new Stroke(styleConfig.stroke) }),
                });
                break;
            case 'point-star':
                imageStyle = new RegularShape({
                    points: styleConfig.points,
                    radius1: styleConfig.outerRadius,
                    radius2: styleConfig.innerRadius,
                    ...(styleConfig.fill && { fill: new Fill(styleConfig.fill) }),
                    ...(styleConfig.stroke && {
                        stroke: new Stroke(styleConfig.stroke),
                    }),
                });
                break;
            case 'point-icon':
                imageStyle = new Icon({
                    // anchor array comes from the api as a JSON object, so convert it to an array
                    src: styleConfig.src,
                    ...(styleConfig.anchor && {
                        anchor: Object.keys(styleConfig.anchor).map((key) => styleConfig.anchor[key]),
                    }),
                    ...(styleConfig.anchorXUnits && { anchorXUnits: styleConfig.anchorXUnits }),
                    ...(styleConfig.anchorYUnits && { anchorYUnits: styleConfig.anchorYUnits }),
                    ...(styleConfig.anchorOrigin && { anchorOrigin: styleConfig.anchorOrigin }),
                    ...(styleConfig.offsetOrigin && { offsetOrigin: styleConfig.offsetOrigin }),
                    ...(styleConfig.color && { color: styleConfig.color as string }),
                    ...(styleConfig.offset && { offset: styleConfig.offset }),
                    ...(styleConfig.opacity && { opacity: styleConfig.opacity }),
                    ...(styleConfig.scale && { scale: styleConfig.scale }),
                    ...(styleConfig.rotateWithView && { rotateWithView: styleConfig.rotateWithView }),
                    ...(styleConfig.rotation && { rotation: styleConfig.rotation }),
                    ...(styleConfig.size && { size: styleConfig.size }),
                    ...(styleConfig.imgSize && { imgSize: styleConfig.imgSize }),
                });
                break;
        }
        return new Style({
            image: imageStyle,
        });
    }

    private getIconUrl$(iconSrc: string): Observable<SVGElement | string> {
        const [a, b] = iconSrc.split(':');

        if (b === undefined) {
            return of(a);
        }

        const prefix = a;
        const path = b;

        switch (prefix) {
            case 'svg':
                return this.iconRegistry.getNamedSvgIcon(path);
            case 'svgUrl':
                return this.iconRegistry.getSvgIconFromUrl(path);
        }
    }

    private getSelectableMsLayers$(): Observable<MsLayerNode[]> {
        return this.root$.pipe(
            map((root) =>
                MapConfigService.traverseLayers(
                    [root],
                    null,
                    (layerName: string, layer: MsLayerNode) => (layer.selectable ? layer : false),
                    () => true
                )
            )
        );
    }

    static traverseLayers(
        context: Array<MsGroupNode | MsLayerNode>,
        targetLayer,
        layerCallback,
        groupCallback
    ): MsLayerNode[] {
        let result = [];
        const obj = context[context.length - 1];
        Object.getOwnPropertyNames(obj).forEach((key) => {
            if (key === 'layers') {
                const layers = obj[key];
                if (typeof targetLayer === 'string' && targetLayer.length > 0) {
                    const foundLayer = layers.find((el) => el.name === targetLayer);
                    if (foundLayer) {
                        const answer = layerCallback.call(null, targetLayer, foundLayer, context);
                        if (answer) {
                            result.push(answer);
                        }
                    }
                } else {
                    layers.forEach((layer) => {
                        const layerName = layer.name;
                        const answer = layerCallback.call(null, layerName, layer, context);
                        if (answer) {
                            result.push(answer);
                        }
                    });
                }
            } else if (key === 'groups') {
                const groups = obj[key];
                groups.forEach((group) => {
                    const groupName = group.name;
                    if (groupCallback.call(null, groupName, group)) {
                        const tmp = MapConfigService.traverseLayers(
                            context.concat(group),
                            targetLayer,
                            layerCallback,
                            groupCallback
                        );
                        result = result.concat(tmp);
                    }
                });
            }
        });
        return result;
    }

    static traverseGroups(obj, targetGroup, groupCallback) {
        const result = [];
        Object.getOwnPropertyNames(obj).forEach((key) => {
            if (key === 'groups') {
                const groups: MsGroupNode[] = obj[key];
                if (typeof targetGroup === 'string' && targetGroup.length > 0) {
                    const foundGroup = groups.find((el) => el.name === targetGroup);
                    if (foundGroup) {
                        const answer = groupCallback.call(null, targetGroup, foundGroup);
                        if (answer) {
                            result.push(answer);
                        }
                    } else {
                        groups.forEach((group) => {
                            const answer = MapConfigService.traverseGroups(group, targetGroup, groupCallback);
                            if (answer) {
                                result.push(answer);
                            }
                        });
                    }
                }
            }
        });
        return result;
    }

    // Property accessors
    get mapName(): string {
        return this.store.selectSnapshot(MapConfigState.mapName);
    }

    get title(): string {
        return this.store.selectSnapshot(MapConfigState.title);
    }

    get crs(): string {
        return this.store.selectSnapshot(MapConfigState.crs);
    }

    get projection(): string {
        return this.store.selectSnapshot(MapConfigState.projection);
    }

    get extent(): Extent {
        return this.store.selectSnapshot(MapConfigState.extent);
    }

    get center(): Coordinate {
        return this.store.selectSnapshot(MapConfigState.center);
    }

    get units(): string {
        return this.store.selectSnapshot(MapConfigState.units);
    }

    get initialExtent(): Extent {
        return this.store.selectSnapshot(MapConfigState.initialExtent);
    }
}
